import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

// Modal Component for Add/Edit Location
const LocationModal = ({ isOpen, onClose, onSubmit, initialData, isEdit }) => {
    const [formData, setFormData] = useState(
        initialData || {
            location_ar: "",
            location_en: "",
            phone_number: "",
            email: "",
            working_hours_ar: "",
            working_hours_en: "",
            download_link: "",
            image: null,
        }
    );
    const [errors, setErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(
        isEdit && initialData?.image_path
            ? `https://ligasos-laravel.ligasos.co/storage/app/public/${initialData.image_path}`
            : null
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "image" && files[0]) {
            setFormData({ ...formData, image: files[0] });
            setImagePreview(URL.createObjectURL(files[0]));
        } else {
            setFormData({ ...formData, [name]: value });
        }
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.location_ar) newErrors.location_ar = "Arabic Location is required";
        if (!formData.location_en) newErrors.location_en = "English Location is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            const data = new FormData();
            data.append("location_ar", formData.location_ar);
            data.append("location_en", formData.location_en);
            data.append("phone_number", formData.phone_number);
            data.append("email", formData.email);
            data.append("working_hours_ar", formData.working_hours_ar);
            data.append("working_hours_en", formData.working_hours_en);
            data.append("download_link", formData.download_link);
            if (formData.image) data.append("image", formData.image);
            await onSubmit(data);
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="custom-container">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">{isEdit ? "Edit Location" : "Add Location"}</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={handleFormSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-white">Location (Arabic)</label>
                        <input
                            type="text"
                            name="location_ar"
                            value={formData.location_ar}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter Arabic location"
                        />
                        {errors.location_ar && <p className="text-red-400 text-sm mt-1">{errors.location_ar}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Location (English)</label>
                        <input
                            type="text"
                            name="location_en"
                            value={formData.location_en}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter English location"
                        />
                        {errors.location_en && <p className="text-red-400 text-sm mt-1">{errors.location_en}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Phone Number (optional)</label>
                        <input
                            type="text"
                            name="phone_number"
                            value={formData.phone_number}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter phone number"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Email (optional)</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter email"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Working Hours (Arabic, optional)</label>
                        <input
                            type="text"
                            name="working_hours_ar"
                            value={formData.working_hours_ar}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter Arabic working hours"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Working Hours (English, optional)</label>
                        <input
                            type="text"
                            name="working_hours_en"
                            value={formData.working_hours_en}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter English working hours"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Download Link (optional)</label>
                        <input
                            type="url"
                            name="download_link"
                            value={formData.download_link}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter download link"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Image (optional)</label>
                        <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#B39359] file:text-white hover:file:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50"
                        />
                        {imagePreview && (
                            <img
                                src={imagePreview}
                                alt="Preview"
                                className="mt-3 w-20 h-20 rounded-full object-cover border-2 border-[#B39359]"
                            />
                        )}
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-[#B39359] text-white hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>{isEdit ? "Update" : "Add"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default function Locations() {
    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editLocation, setEditLocation] = useState(null);
    const token = localStorage.getItem("token");

    // Fetch all locations
    useEffect(() => {
        const fetchLocations = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get("https://ligasos-laravel.ligasos.co/api/locations", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setLocations(response.data);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch locations. Please try again.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchLocations();
    }, [token]);

    // Handle Add Location
    const handleAddLocation = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                "https://ligasos-laravel.ligasos.co/api/locations",
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setLocations([...locations, response.data.data]);
            setIsAddModalOpen(false);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Location added successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to add location.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Edit Location
    const handleEditLocation = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `https://ligasos-laravel.ligasos.co/api/locations/${editLocation.id}?_method=PUT`,
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setLocations(locations.map((l) => (l.id === editLocation.id ? response.data.data : l)));
            setIsEditModalOpen(false);
            setEditLocation(null);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Location updated successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to update location.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Delete Location
    const handleDeleteLocation = async (id) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#B39359",
            cancelButtonColor: "#dc2626",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axios.delete(`https://ligasos-laravel.ligasos.co/api/locations/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setLocations(locations.filter((l) => l.id !== id));
                MySwal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: "Location deleted successfully!",
                    confirmButtonColor: "#B39359",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to delete location.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <section className="p-8 min-h-screen">
            <div className="">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-extrabold text-[#0B2C3D] tracking-tight">Locations</h2>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        className="flex items-center gap-2 bg-[#B39359] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 shadow-md"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Location
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B39359]"></div>
                    </div>
                ) : locations.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg font-medium">No locations found.</p>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#0B2C3D] sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Image</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Location (Arabic)</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Location (English)</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Phone Number</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {locations.map((location, index) => (
                                    <tr
                                        key={location.id}
                                        className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-4">
                                            {location.image_path ? (
                                                <img
                                                    src={`https://ligasos-laravel.ligasos.co/storage/app/public/${location.image_path}`}
                                                    alt={location.location_en}
                                                    className="w-12 h-12 object-cover border-2 border-[#B39359]"
                                                    onError={(e) => (e.target.src = "https://via.placeholder.com/40")}
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">
                                                    No Image
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{location.location_ar || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{location.location_en || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{location.phone_number || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{location.email || "N/A"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditLocation(location);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteLocation(location.id)}
                                                    className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modals */}
            <LocationModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddLocation}
                isEdit={false}
            />
            <LocationModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditLocation(null);
                }}
                onSubmit={handleEditLocation}
                initialData={editLocation}
                isEdit={true}
            />
        </section>
    );
}